/** Format family for a forecast SKU, from Cin7 product name, SKU, and litres per unit. */

const KEG_SIZES = [50, 30, 20, 19.5];

const KEG_LITRES_RE = /(\d+(?:\.\d+)?)\s*(?<!m)l(?:itre|iter)?s?\s*(?:nitro\s*)?(?:keg|kegstar|firkin)\b/;
const LOOSE_LITRES_RE = /(\d+(?:\.\d+)?)\s*(?<!m)l(?:itre|iter)?s?\b/;
const KEG_WORD_RE = /\b(?:kegs?|kegstars?|firkins?)\b/;
const SIX_RE = /\b(?:six\s*packs?|6\s*-?\s*packs?|6pk|4\s*[x×]\s*6)\b|\b6\s*[x×]\s*\d{3}(?!\d)/;
const FOUR_RE = /\b(?:4\s*-?\s*packs?|4pk|6\s*[x×]\s*4)\b|\b4\s*[x×]\s*\d{3}(?!\d)/;
const NUMBERED_PACK_RE = /\b(\d+)\s*-?\s*packs?\b|\b(\d+)pk\b/;
/** "2x12 pack" has no word break before 12, so NUMBERED_PACK_RE misses it. Inner count is the pack. */
const CASE_OF_PACKS_RE = /(\d+)\s*[x×]\s*(\d+)\s*-?\s*packs?\b/;

export const FAMILIES = [
  { id: 'all', label: 'All' },
  { id: 'keg', label: 'Kegs' },
  { id: 'can', label: 'Cans' },
  { id: 'bottle', label: 'Bottles' },
];

const lineText = (line) => `${line.productName || ''} ${line.sku || ''}`.toLowerCase();

const nearestKegSize = (litres) => {
  const n = Number(litres);
  if (!(n > 0)) return null;
  let best = null;
  let bestDelta = Infinity;
  KEG_SIZES.forEach((size) => {
    const delta = Math.abs(n - size) / size;
    if (delta <= 0.05 && delta < bestDelta) {
      best = size;
      bestDelta = delta;
    }
  });
  return best;
};

const litresFrom = (text, re) => {
  const match = text.match(re);
  if (!match) return null;
  const n = Number(match[1]);
  return n > 0 && n <= 100 ? n : null;
};

const kegLitresFromText = (text) => litresFrom(text, KEG_LITRES_RE);

/** "10.5L" sitting away from the word keg / firkin, once we already know it is a keg. */
const looseLitres = (text) => litresFrom(text, LOOSE_LITRES_RE);

const sizeLabel = (size) => String(size);

const kegFormat = (size) => {
  if (size == null) {
    return {
      family: 'keg',
      formatId: 'keg:other',
      formatLabel: 'Keg',
      shortLabel: 'Keg',
      sort: 90,
    };
  }
  const label = sizeLabel(size);
  return {
    family: 'keg',
    formatId: `keg:${label}`,
    formatLabel: `${label}L keg`,
    shortLabel: `${label}L`,
    sort: 100 - size,
  };
};

const formatRow = (family, id, formatLabel, shortLabel, sort) => ({
  family,
  formatId: `${family}:${id}`,
  formatLabel,
  shortLabel,
  sort,
});

const slabMatch = (text) => {
  const dotted = text.match(/\b(\d+)\s*[x×]\s*\.(\d{3})\b/);
  const withMl = text.match(/\b(\d+)\s*[x×]\s*(\d{2,4})\s*ml\b/);
  const compact = text.match(/\b(\d+)\s*[x×]\s*(\d{3})\b/);
  const match = dotted || withMl || compact;
  if (match) {
    const count = Number(match[1]);
    const ml = Number(match[2]);
    if (count >= 8 && ml >= 200 && ml <= 1000) return { count, ml };
  }
  if (/\bslab\b/.test(text)) return { count: null, ml: null };
  return null;
};

const packFormat = (text, family) => {
  const noun = family === 'bottle' ? 'bottle' : 'can';
  if (SIX_RE.test(text)) return formatRow(family, 'six_pack', 'Six pack', 'Six pack', 200);
  if (FOUR_RE.test(text)) return formatRow(family, 'four_pack', '4-pack', '4-pack', 210);
  const caseOfPacks = text.match(CASE_OF_PACKS_RE);
  if (caseOfPacks) {
    const packSize = Number(caseOfPacks[2]);
    if (packSize >= 2 && packSize <= 36 && packSize !== 4 && packSize !== 6) {
      return formatRow(family, `pack_${packSize}`, `${packSize}-pack`, `${packSize}-pack`, 230);
    }
  }
  const numbered = text.match(NUMBERED_PACK_RE);
  if (numbered) {
    const n = Number(numbered[1] || numbered[2]);
    if (n >= 2 && n <= 36 && n !== 4 && n !== 6) {
      return formatRow(family, `pack_${n}`, `${n}-pack`, `${n}-pack`, 230);
    }
  }
  const slab = slabMatch(text);
  if (slab && slab.count && slab.ml) {
    return formatRow(
      family,
      `slab_${slab.count}x${slab.ml}`,
      `${slab.count} × ${slab.ml}ml`,
      `${slab.count}×${slab.ml}`,
      100 + (48 - Math.min(slab.count, 48)),
    );
  }
  if (slab) return formatRow(family, 'slab', 'Slab', 'Slab', 180);
  const ml = text.match(/\b(\d{2,4})\s*ml\b/);
  if (ml) {
    return formatRow(family, `single_${ml[1]}`, `${ml[1]}ml ${noun}`, `${ml[1]}ml`, 300);
  }
  const label = family === 'bottle' ? 'Bottle' : 'Can';
  return formatRow(family, family, label, label, 400);
};

const looksPacked = (text) => (
  SIX_RE.test(text) || FOUR_RE.test(text) || CASE_OF_PACKS_RE.test(text)
  || NUMBERED_PACK_RE.test(text) || slabMatch(text) != null
);

/** @returns {{ family, formatId, formatLabel, shortLabel, sort }} */
export const classifySku = (line) => {
  const text = lineText(line || {});
  const bottle = /\bbottles?\b/.test(text);
  const can = /\bcans?\b/.test(text);
  const namedKeg = kegLitresFromText(text);
  const kegWord = KEG_WORD_RE.test(text);
  const kegSize = nearestKegSize(line && line.litresPerUnit);
  const kegByVolume = kegSize != null && !bottle && !can && !looksPacked(text);
  if (kegWord || namedKeg != null || kegByVolume) {
    let fromName = kegSize;
    if (namedKeg != null) fromName = namedKeg;
    else if (kegWord) fromName = looseLitres(text) || kegSize;
    return kegFormat(fromName);
  }
  if (bottle) return packFormat(text, 'bottle');
  if (can || looksPacked(text)) return packFormat(text, 'can');
  return {
    family: 'other',
    formatId: 'other',
    formatLabel: 'Other',
    shortLabel: 'Other',
    sort: 500,
  };
};

/** A forecast SKU line with any packaged stock anywhere. */
export const isStocked = (line) => {
  if (!line) return false;
  if ((line.stockLitres || 0) > 0 || (line.stockUnits || 0) > 0) return true;
  return Object.values(line.stockUnitsByBucket || {}).some((n) => n > 0);
};

/** Formats present on these classified rows, optionally limited to one family. */
export const formatsFor = (rows, family) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    if (family && family !== 'all' && row.family !== family) return;
    if (!map.has(row.formatId)) {
      map.set(row.formatId, {
        id: row.formatId,
        label: row.formatLabel,
        sort: row.sort,
      });
    }
  });
  return [...map.values()].sort((a, b) => a.sort - b.sort || a.label.localeCompare(b.label));
};
